import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../api';
import { MOCK_COMPETITIONS, MOCK_TEAMS, TEAM_SIZE_LIMITS, MOCK_JOIN_REQUESTS, STORAGE_KEYS, API_ENDPOINTS, USER_ROLES } from '../config';
import Leaderboard from './Leaderboard';
import Dashboard from './Dashboard';
import SettingsMenu from './SettingsMenu';
import { useI18n } from '../i18n';
import './CompetitionPage.css';

const TeamOnboarding = ({ teams, pendingTeam, joinLocked, onCreate, onJoin, t }) => {
  const sortedTeams = [...teams].sort((a, b) => {
    const aCount = a.members ? a.members.length : (a.member_count || 0);
    const bCount = b.members ? b.members.length : (b.member_count || 0);
    const aCap = a.capacity || a.capacity_limit || 0;
    const bCap = b.capacity || b.capacity_limit || 0;
    const aFull = aCap > 0 ? aCount >= aCap : false;
    const bFull = bCap > 0 ? bCount >= bCap : false;
    if (aFull === bFull) return (bCount - aCount);
    return aFull ? 1 : -1;
  });

  const displayTeams = pendingTeam
    ? [pendingTeam, ...sortedTeams.filter((t) => t.id !== pendingTeam.id)]
    : sortedTeams;

  return (
    <div className="team-onboarding">
      <div className="team-onboarding-header">
        <h2>{t('team.onboarding.title')}</h2>
        <button className="primary-btn" onClick={onCreate} disabled={joinLocked}>{t('team.onboarding.create')}</button>
      </div>
      <div className="team-onboarding-list">
        {displayTeams.map((team) => {
          const count = team.members ? team.members.length : (team.member_count || 0);
          const capacity = team.capacity || team.capacity_limit || 0;
          const isFull = capacity > 0 ? count >= capacity : false;
          const isPending = pendingTeam && pendingTeam.id === team.id && pendingTeam.status === 'pending';
          const isRejected = pendingTeam && pendingTeam.id === team.id && pendingTeam.status === 'rejected';

          return (
            <div key={team.id} className={`team-row ${isFull ? 'full' : ''}`}>
              <div className="team-row-main">
                <div className="team-row-title">
                  <h4>{team.name || team.team_name}</h4>
                  <span className={`status-badge status-${isFull ? 'closed' : 'active'}`}>
                    {isPending ? t('team.status.pending') : (isRejected ? t('team.status.rejected') : (isFull ? t('team.status.full') : t('team.status.open')))}
                  </span>
                </div>
                <div className="team-row-meta">
                  <span>{t('team.captain')}：{team.captain || team.owner || team.leader || '—'}</span>
                  <span>{t('team.members')}：{capacity > 0 ? `${count}/${capacity}` : count}</span>
                  {team.description && <span className="team-desc">{team.description}</span>}
                </div>
              </div>
              <div className="team-row-actions">
                <button
                  className="primary-btn"
                  disabled={isFull || joinLocked}
                  onClick={() => onJoin(team)}
                >
                  {isPending ? t('team.status.pending') : (isFull ? t('team.join.disabled') : t('team.apply'))}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CompetitionPage = () => {
  const { competitionId } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'leaderboard');
  const [teamReady, setTeamReady] = useState(false);
  const [teams, setTeams] = useState(MOCK_TEAMS);
  const [myTeam, setMyTeam] = useState(null);
  const [pendingTeam, setPendingTeam] = useState(null);
  const [joinLocked, setJoinLocked] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamSize, setNewTeamSize] = useState(TEAM_SIZE_LIMITS.min);
  const [newTeamDescription, setNewTeamDescription] = useState('');
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinTarget, setJoinTarget] = useState(null);
  const [joinMessage, setJoinMessage] = useState('');
  const [applications, setApplications] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, basePath } = useI18n();
  const isLoggedIn = localStorage.getItem(STORAGE_KEYS.isLoggedIn) === 'true';
  const username = localStorage.getItem(STORAGE_KEYS.username) || 'guest';
  const registryUrl = localStorage.getItem(STORAGE_KEYS.registryUrl) || '—';
  const role = localStorage.getItem(STORAGE_KEYS.role) || USER_ROLES.user;
  const hasJoinRequests = applications.length > 0;
  const getStatusLabel = (status, fallback) => t(`competition.status.${status}`) || fallback || status;

  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        setLoading(true);
        const data = await apiRequest(API_ENDPOINTS.allCompetitions);
        const competitions = Array.isArray(data) ? data : MOCK_COMPETITIONS;
        const found = competitions.find((c) => String(c.id) === String(competitionId));
        
        if (found) {
          setCompetition(found);
        } else {
          setCompetition(null);
        }
      } catch (err) {
        console.warn('Failed to fetch competition, using mock data:', err);
        const found = MOCK_COMPETITIONS.find((c) => String(c.id) === String(competitionId));
        setCompetition(found || null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompetition();
  }, [competitionId]);

  const roleLabel = role === USER_ROLES.siteAdmin
    ? t('role.siteAdmin')
    : role === USER_ROLES.competitionAdmin
      ? t('role.competitionAdmin')
      : t('role.user');

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.isLoggedIn);
    localStorage.removeItem(STORAGE_KEYS.username);
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.registryUrl);
    localStorage.removeItem(STORAGE_KEYS.role);
    navigate(`${basePath}/leaderboard`);
  };

  const fetchMyTeam = useCallback(async () => {
    try {
      const status = await apiRequest(API_ENDPOINTS.userTeamStatus(competitionId));
      if (!status) {
        setTeamReady(false);
        setPendingTeam(null);
        setJoinLocked(false);
        return;
      }

      if (status.status === 'pending') {
        setTeamReady(false);
        setPendingTeam(status);
        setJoinLocked(true);
        setMyTeam(null);
        return;
      }

      if (status.status === 'rejected') {
        setTeamReady(false);
        setPendingTeam(status);
        setJoinLocked(false);
        setMyTeam(null);
        return;
      }

      setTeamReady(true);
      setPendingTeam(null);
      setJoinLocked(false);
      setMyTeam(status);
    } catch (err) {
      console.warn('获取用户队伍状态失败，保持默认:', err.message);
    }
  }, [competitionId]);

  useEffect(() => {
    if (!isLoggedIn) {
      if (activeTab !== 'leaderboard') {
        setActiveTab('leaderboard');
      }
      return;
    }
    const fetchTeams = async () => {
      try {
        const data = await apiRequest(API_ENDPOINTS.allTeams(competitionId));
        const list = Array.isArray(data) ? data : [];
        setTeams(list);
      } catch (err) {
        console.warn('Backend fetch failed, using mock data');
        setTeams(MOCK_TEAMS);
      }
    };

    fetchTeams();
    fetchMyTeam();
  }, [competitionId, isLoggedIn, activeTab, fetchMyTeam]);

  const isCaptain = useMemo(() => {
    if (!myTeam || !username) return false;
    const captainName = myTeam.captain || myTeam.owner || myTeam.leader;
    return captainName === username;
  }, [myTeam, username]);

  useEffect(() => {
    if (activeTab === 'members' && teamReady && isCaptain) {
      const fetchApps = async () => {
        try {
          const data = await apiRequest(API_ENDPOINTS.allApplications(competitionId));
          setApplications(Array.isArray(data) ? data : []);
        } catch (err) {
          console.warn('Using mock apps');
          setApplications(MOCK_JOIN_REQUESTS);
        }
      };
      fetchApps();
    }
  }, [activeTab, teamReady, isCaptain, competitionId]); // 添加 isCaptain 和 competitionId

  const handleOpenCreate = () => {
    if (joinLocked) return;
    setShowCreateModal(true);
  };

  const handleCreateTeam = async () => {
    try {
      await apiRequest(API_ENDPOINTS.createTeam, {
        method: 'POST',
        body: JSON.stringify({
          competition_id: parseInt(competitionId, 10),
          team_name: newTeamName,
          description: newTeamDescription || t('team.create.descDefault')
        })
      });
      setShowCreateModal(false);
      await fetchMyTeam();
      setActiveTab('members');
    } catch (err) {
      console.warn('创建失败: ' + err.message);
    }
  };

  const handleOpenJoin = (team) => {
    if (joinLocked) return;
    setJoinTarget(team);
    setJoinMessage('');
    setShowJoinModal(true);
  };

  const handleSubmitJoin = async () => {
    if (!joinTarget) return;
    try {
      await apiRequest(API_ENDPOINTS.joinTeam, {
        method: 'POST',
        body: JSON.stringify({
          team_id: joinTarget.id,
          message: joinMessage || t('modal.join.title')
        })
      });
      setPendingTeam({ ...joinTarget, status: 'pending' });
      setJoinLocked(true);
      setShowJoinModal(false);
    } catch (err) {
      console.warn('申请失败: ' + err.message);
      setShowJoinModal(false);
    }
  };

  const handleReviewApplication = async (appId, action) => {
    try {
      await apiRequest(API_ENDPOINTS.reviewApplication, {
        method: 'POST',
        body: JSON.stringify({
          application_id: appId,
          action: action
        })
      });
      // Update local state
      setApplications(prev => prev.filter(app => app.application_id !== appId));
      alert(action === 'approve' ? t('action.approve') : t('action.reject'));
    } catch (err) {
      alert(`${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="competition-page">
        <div className="competition-empty">
          <h2>{t('competition.loading') || '加载中...'}</h2>
        </div>
      </div>
    );
  }

  if (!competition) {
    return (
      <div className="competition-page">
        <div className="competition-empty">
          <h2>{t('competition.notFound')}</h2>
          <Link to={`${basePath}/leaderboard/competitions`} className="secondary-btn">{t('competition.backToLobby')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="competition-page">
      <nav className="navbar">
        <div className="nav-left">
          <div className="nav-brand">
          <div className="nav-title-group">
            <span className="nav-title">{competition.name}</span>
            <span className="nav-subtitle">{competition.description}</span>
          </div>
          </div>
          <span className={`status-badge status-${competition.status}`}>{getStatusLabel(competition.status, competition.statusLabel)}</span>
          <span className="nav-meta">{t('competition.deadline')}：{competition.deadline}</span>
        </div>
        
        <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? '✕' : '☰'}
        </button>

        <div className={`nav-right ${isMenuOpen ? 'open' : ''}`}>
          <Link to={`${basePath}/leaderboard`} className="nav-link">{t('nav.home')}</Link>
          <Link to={`${basePath}/leaderboard/competitions`} className="nav-link">{t('nav.competitions')}</Link>
          <button
            className={activeTab === 'leaderboard' ? 'active' : ''}
            onClick={() => setActiveTab('leaderboard')}
          >
            {t('competition.leaderboard')}
          </button>
          {isLoggedIn && !teamReady && (
            <button
              className={activeTab === 'teams' ? 'active' : ''}
              onClick={() => setActiveTab('teams')}
            >
              {t('competition.teamJoin')}
            </button>
          )}
          
          {isLoggedIn && teamReady && (
            <>
              <button
                className={activeTab === 'dashboard' ? 'active' : ''}
                onClick={() => setActiveTab('dashboard')}
              >
                {t('competition.myData')}
              </button>
              <button
                className={activeTab === 'members' ? 'active' : ''}
                onClick={() => setActiveTab('members')}
              >
                {t('competition.members')}
                {hasJoinRequests && <span className="tab-dot" />}
              </button>
            </>
          )}
          <SettingsMenu />
          {isLoggedIn ? (
            <div className="user-menu">
              <div className="user-menu-trigger">👤 {username}</div>
              <div className="user-menu-panel">
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.username')}</span>
                  <span>{username}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.role')}</span>
                  <span>{roleLabel}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">{t('user.registry')}</span>
                  <span>{registryUrl}</span>
                </div>
                <button className="user-menu-logout" onClick={handleLogout}>{t('user.logout')}</button>
              </div>
            </div>
          ) : (
            <Link to={`${basePath}/leaderboard/login`} className="nav-link nav-login">{t('nav.login')}</Link>
          )}
        </div>
      </nav>

      <div className="competition-content">
        {activeTab === 'leaderboard' && <Leaderboard showNav={false} competitionId={competitionId} />}
        {activeTab === 'teams' && isLoggedIn && !teamReady && (
          <TeamOnboarding
            teams={teams}
            pendingTeam={pendingTeam}
            joinLocked={joinLocked}
            onCreate={handleOpenCreate}
            onJoin={handleOpenJoin}
            t={t}
          />
        )}
        {activeTab === 'teams' && !isLoggedIn && (
          <div className="competition-placeholder">
            <h3>{t('competition.loginFirst')}</h3>
            <Link to={`${basePath}/leaderboard/login`} className="primary-btn">{t('competition.toLogin')}</Link>
          </div>
        )}
        {activeTab === 'dashboard' && teamReady && <Dashboard showNav={false} />}
        {activeTab === 'members' && teamReady && (
          <div className="competition-placeholder">
            <h3>{t('competition.membersManage')}</h3>
            
            {myTeam && (
              <div className="team-info-card-container">
                <h4 className="team-info-title">{myTeam.name || myTeam.team_name}</h4>
                <div className="team-info-content">
                  <p><strong>{t('team.captain')}：</strong>{myTeam.captain || myTeam.owner || myTeam.leader}</p>
                  <p><strong>{t('team.description')}：</strong>{myTeam.description || 'N/A'}</p>
                  
                  {myTeam.members && (
                    <div className="team-members-list-wrapper">
                      <strong>{t('team.members')} ({myTeam.members.length}):</strong>
                      <ul className="team-members-list">
                        {myTeam.members.map((m, idx) => (
                           <li key={idx} className="team-member-item">
                             👤 {typeof m === 'object' ? (m.username || m.name) : m}
                           </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {isCaptain ? (
              <div className="team-requests">
                <h4>{t('competition.requests')}</h4>
                {applications.map((req) => (
                  <div key={req.application_id || req.id} className="request-item">
                    <div>
                      <strong>{req.username || req.applicant || req.user}</strong> {t('competition.requestedJoin')}
                      <div><small>{req.message || ''}</small></div>
                    </div>
                    <div className="request-actions">
                      <button className="secondary-btn" onClick={() => handleReviewApplication(req.application_id, 'approve')}>{t('action.approve')}</button>
                      <button className="ghost-btn" onClick={() => handleReviewApplication(req.application_id, 'reject')}>{t('action.reject')}</button>
                    </div>
                  </div>
                ))}
                {applications.length === 0 && (
                  <p className="empty-note">{t('competition.noRequests')}</p>
                )}
              </div>
            ) : (
              <div className="team-member-view">
                 <p style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Only the team captain can manage membership requests.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('modal.create.title')}</h3>
            <label>
              {t('modal.create.name')}
              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                placeholder={t('team.create.namePlaceholder')}
              />
            </label>
            <label>
              {t('modal.create.desc')}
              <input
                type="text"
                value={newTeamDescription}
                onChange={(e) => setNewTeamDescription(e.target.value)}
                placeholder={t('team.create.descPlaceholder')}
              />
            </label>
            <label>
              {t('modal.create.size')}
              <input
                type="number"
                min={TEAM_SIZE_LIMITS.min}
                max={TEAM_SIZE_LIMITS.max}
                value={newTeamSize}
                onChange={(e) => setNewTeamSize(Number(e.target.value))}
              />
            </label>
            <div className="team-limit">{t('team.create.sizeRange')}：{TEAM_SIZE_LIMITS.min} - {TEAM_SIZE_LIMITS.max}</div>
            <div className="modal-actions">
              <button className="ghost-btn" onClick={() => setShowCreateModal(false)}>{t('modal.create.cancel')}</button>
              <button className="primary-btn" onClick={handleCreateTeam}>{t('modal.create.confirm')}</button>
            </div>
          </div>
        </div>
      )}

      {showJoinModal && (
        <div className="modal-backdrop" onClick={() => setShowJoinModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('modal.join.title')}</h3>
            <div className="modal-subtitle">{t('modal.join.target')}：{joinTarget?.name || joinTarget?.team_name}</div>
            <label>
              {t('modal.join.message')}
              <textarea
                value={joinMessage}
                onChange={(e) => setJoinMessage(e.target.value)}
                placeholder={t('team.join.placeholder')}
                rows={4}
              />
            </label>
            <div className="modal-actions">
              <button className="ghost-btn" onClick={() => setShowJoinModal(false)}>{t('modal.create.cancel')}</button>
              <button className="primary-btn" onClick={handleSubmitJoin}>{t('modal.join.submit')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompetitionPage;
